import { useTranslation } from 'react-i18next';
import { years, eurosMWp, eurosMW, europeanFormatNumber } from '@/helpers';
import { Button, Icon, Typography } from '@/components';

export type CapexOpexBoxProps = {
  readonly normalizedProject: any;
  readonly onEdit?: () => void;
  readonly isEditable: boolean;
};

export function CapexOpexBox({ normalizedProject, isEditable, onEdit }: CapexOpexBoxProps) {
  const { t } = useTranslation(['projects']);

  const capex_opex = normalizedProject.capexAndOpex?.[0];
  const technology = normalizedProject.technologies;

  if (!capex_opex && !isEditable) return null;

  const hasWind = technology.some((t: any) => t.type === 'wind');
  const suffix = technology.some((t: any) => t.type === 'solar') ? eurosMWp : eurosMW;

  const allFields = [
    { label: t('Estimated EPC'), value: suffix(capex_opex?.estimated_epc) },
    {
      label: t('EPC comments'),
      value: (
        <span className="text-base font-normal whitespace-pre-wrap text-neutral-900">{capex_opex?.epc_comments}</span>
      ),
    },
    ...(hasWind
      ? [
          {
            label: t('TSA'),
            value: capex_opex?.tsa != null ? `${europeanFormatNumber(capex_opex.tsa)}  €/year` : null,
          },
          { label: t('TSA Details'), value: capex_opex?.tsa_details },
          { label: t('BOP'), value: capex_opex?.bop != null ? `${europeanFormatNumber(capex_opex.bop)} €/year` : null },
          { label: t('BOP Details'), value: capex_opex?.bop_details },
        ]
      : []),
    {
      label: t('O&M price'),
      value: capex_opex?.om_price != null ? `${europeanFormatNumber(capex_opex.om_price)} €/year` : null,
    },
    {
      label: t('O&M'),
      value: capex_opex?.om_term != null ? years(capex_opex.om_term) : null,
    },
    {
      label: t('O&M Comments'),
      value: (
        <span className="text-base font-normal whitespace-pre-wrap text-neutral-900">{capex_opex?.om_comments}</span>
      ),
    },
    {
      label: t('Other Comments'),
      value: (
        <span className="text-base font-normal whitespace-pre-wrap text-neutral-900">{capex_opex?.other_comments}</span>
      ),
    },
  ];

  const visibleFields = isEditable
    ? allFields
    : allFields.filter(({ value }) => value !== null && value !== undefined && value !== '');

  if (!isEditable && visibleFields.length === 0) return null;

  return (
    <div className="rounded-lg border border-neutral-50 bg-white p-6 shadow-xs">
      <div className="mb-6 flex items-center">
        <div className="flex grow items-center">
          <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-50">
            <Icon name="settings" className="h-6 w-6" />
          </div>
          <Typography as="h3" weight="semibold" size="title">
            {t('Capex & Opex')}
          </Typography>
        </div>
        {isEditable && (
          <Button size="sm" color="neutral" icon="edit" onClick={onEdit} className="ml-auto">
            {t('Edit')}
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1">
        {visibleFields.map(({ label, value }) => (
          <Typography key={label} as="p" size="body-2" weight="semibold" relaxed className="mb-1">
            {label}:{' '}
            <span className="ml-3 text-base font-normal text-neutral-900">{value ?? (isEditable ? '—' : null)}</span>
          </Typography>
        ))}
      </div>
    </div>
  );
}
